import {
  type PageTemplateChoices,
  type PickableStorefrontPage,
  resolveStorefrontPageTemplates,
  STOREFRONT_PAGE_TEMPLATE_IDS,
  STOREFRONT_PAGE_TEMPLATE_INFO,
  STOREFRONT_TEMPLATE_PAGES,
  type StorefrontPage,
  type StorefrontTemplateId,
} from "@loot-os/api-contract";

import type { PreviewPageId } from "./themePreview";

/** The pages a store can pick its own layout for, in the order the Pages page lists them. */
export const PICKABLE_PAGES = Object.keys(
  STOREFRONT_PAGE_TEMPLATE_INFO,
) as readonly PickableStorefrontPage[];

/** The Pages page's unsaved picks, by page. */
export type PageTemplateDrafts = Partial<Record<StorefrontPage, string>>;

/** The layout `page` gets from the storefront template alone. */
export function templateLayout(base: StorefrontTemplateId, page: StorefrontPage): string {
  return STOREFRONT_TEMPLATE_PAGES[base][page];
}

/** The layout `page` renders: the store's saved pick, else the storefront template's. */
export function currentLayout(
  base: StorefrontTemplateId,
  saved: PageTemplateChoices,
  page: StorefrontPage,
): string {
  return resolveStorefrontPageTemplates(base, saved)[page];
}

/**
 * What saving `id` for `page` stores: the id, or null when the storefront template already draws
 * the page that way, so the page keeps following the template.
 */
export function toPick(
  base: StorefrontTemplateId,
  page: StorefrontPage,
  id: string,
): string | null {
  return id === templateLayout(base, page) ? null : id;
}

/** The picks the preview draws: the saved ones with the unsaved ones over them. */
export function previewPicks(
  saved: PageTemplateChoices,
  drafts: PageTemplateDrafts,
): Partial<Record<StorefrontPage, string>> {
  return { ...saved, ...drafts };
}

/**
 * The preview's page for a storefront page. The preview draws every page, so it is the page
 * itself; a page left out of `PREVIEW_PAGES` fails to compile here.
 */
export function previewPageOf(page: StorefrontPage): PreviewPageId {
  return page;
}

/**
 * The layouts every one of `pages` offers, in the first page's order: what a group of pages can be
 * set to at once. Fewer than two, and the group has nothing to pick.
 */
export function sharedLayouts(pages: readonly StorefrontPage[]): string[] {
  const [first, ...rest] = pages.map(
    (page) => STOREFRONT_PAGE_TEMPLATE_IDS[page] as readonly string[],
  );
  if (!first) return [];
  return first.filter((id) => rest.every((ids) => ids.includes(id)));
}

/** The layout every page of a group renders, or null when they differ. */
export function sharedLayout(layouts: readonly string[]): string | null {
  const [first] = layouts;
  return first !== undefined && layouts.every((id) => id === first) ? first : null;
}
